import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit2, Save, MoreHorizontal, ExternalLink, Star } from 'lucide-react';
import { Button } from '../../ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import StatusPill from '../StatusPill';

const money = (value) => {
  const n = Number(value || 0);
  if (Math.abs(n) >= 1000) return `€${Math.round(n / 1000).toLocaleString()}K`;
  return `€${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

/**
 * Operational header for the project workspace: identity, status, the numbers
 * that matter and contextual actions. Tab navigation lives here too so mobile
 * gets a compact section selector instead of a row of squeezed tabs.
 */
export const ProjectWorkspaceHeader = ({
  project,
  overview,
  tabs,
  activeTab,
  onTabChange,
  editMode,
  isEditable,
  saving,
  onEdit,
  onCancelEdit,
  onSave,
  extraActions = [],
}) => {
  const navigate = useNavigate();
  const stats = overview?.project;
  const instrument = overview?.instrument;
  const activeTabLabel = tabs.find((t) => t.value === activeTab)?.label;

  return (
    <div className="mb-6" data-testid="project-workspace-header">
      <button
        onClick={() => navigate('/admin/v2/projects')}
        className="inline-flex items-center gap-1.5 text-xs text-foreground-muted hover:text-slate-900 mb-3"
        data-testid="back-to-projects-btn"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Projects
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-xl md:text-2xl font-heading truncate">{project.title}</h1>
            <StatusPill status={project.status} testId="project-status-pill" />
            {project.featured && <StatusPill status="active" label="Featured" tone="amber" dot={false} />}
          </div>
          <p className="text-xs text-foreground-muted mt-1.5">
            <span className="font-mono">{project.code}</span>
            {project.project_type ? ` · ${String(project.project_type).replace(/_/g, ' ')}` : ''}
            {stats?.location ? ` · ${stats.location}` : ''}
          </p>
        </div>

        <div className="flex items-center gap-2">
          {editMode ? (
            <>
              <Button variant="outline" size="sm" onClick={onCancelEdit}>Cancel</Button>
              <Button size="sm" onClick={onSave} disabled={saving} data-testid="save-project-btn">
                <Save className="h-4 w-4 mr-1.5" />
                {saving ? 'Saving…' : 'Save changes'}
              </Button>
            </>
          ) : (
            <>
              {isEditable && (
                <Button size="sm" onClick={onEdit} data-testid="edit-project-btn">
                  <Edit2 className="h-4 w-4 mr-1.5" /> Edit project
                </Button>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="project-more-actions">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <DropdownMenuItem onClick={() => window.open(`/projects/${project.id}`, '_blank')}>
                    <ExternalLink className="h-4 w-4 mr-2" /> Preview investor view
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onTabChange('instruments')}>Manage instrument</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onTabChange('financials')}>Update financials</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onTabChange('assets')}>Manage asset</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onTabChange('workflow')}>
                    <Star className="h-4 w-4 mr-2" /> Publishing &amp; workflow
                  </DropdownMenuItem>
                  {extraActions.map((action) => (
                    <DropdownMenuItem key={action.label} onClick={action.onClick}>{action.label}</DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}
        </div>
      </div>

      {/* Key operational numbers */}
      {stats && (
        <div className="mt-5 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm" data-testid="project-header-stats">
          <span><span className="font-semibold tabular-nums">{money(stats.funding_raised)}</span> <span className="text-foreground-muted">raised</span></span>
          <span><span className="font-semibold tabular-nums">{stats.funding_progress || 0}%</span> <span className="text-foreground-muted">funded</span></span>
          <span><span className="font-semibold tabular-nums">{(stats.units_available || 0).toLocaleString()}</span> <span className="text-foreground-muted">units available</span></span>
          {instrument?.max_hold_period_months && (
            <span><span className="font-semibold tabular-nums">{instrument.max_hold_period_months}mo</span> <span className="text-foreground-muted">target hold</span></span>
          )}
          <span><span className="font-semibold tabular-nums">{stats.investors_count || 0}</span> <span className="text-foreground-muted">investors</span></span>
        </div>
      )}

      {/* Section navigation */}
      <div className="mt-6">
        <div className="sm:hidden">
          <Select value={activeTab} onValueChange={onTabChange}>
            <SelectTrigger className="w-full" data-testid="mobile-tab-selector">
              <SelectValue>{activeTabLabel}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {tabs.map((tab) => (
                <SelectItem key={tab.value} value={tab.value}>{tab.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="hidden sm:flex items-center gap-1 border-b border-black/[0.06] -mx-1 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.value}
              onClick={() => onTabChange(tab.value)}
              className={`px-3 py-2.5 text-sm whitespace-nowrap border-b-2 -mb-px transition-colors ${
                activeTab === tab.value
                  ? 'border-primary text-slate-900 font-medium'
                  : 'border-transparent text-foreground-muted hover:text-slate-900'
              }`}
              data-testid={`project-tab-${tab.value}`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProjectWorkspaceHeader;
